import { useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import {
  ArrowRight,
  ChevronRight,
  FileText,
  FolderOpen,
  Lock,
  Sparkles,
  X,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import DashboardPromo from "@/components/tuw/DashboardPromo"
import ResumeDraftBanner from "@/components/tuw/ResumeDraftBanner"
import { Progress } from "@/components/ui/progress"
import { SERVICES, BUNDLE, serviceHref } from "@/lib/services"
import { cn } from "@/lib/utils"

// Time-of-day greeting. Purely cosmetic (the customer's first name is real);
// the device clock decides morning/afternoon/evening.
function greetingFor(firstName) {
  const hour = new Date().getHours()
  const part =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening"
  return firstName ? `${part}, ${firstName}` : `${part}`
}

// ---- announcement ---------------------------------------------------------

// The latest admin-set customer announcement (real Announcement record). Shows
// a "New" flag while unread; dismiss hides it for the session.
function AnnouncementBanner({ announcement }) {
  const [dismissed, setDismissed] = useState(false)
  if (!announcement?.header || dismissed) return null

  return (
    // MOBILE-PLAN :47 — "the announce/upsell/resume banners wrap at ≤560".
    // Prototype `@media (max-width:560px)` (customer-dashboard-light.html
    // :328-332): `.announce{flex-wrap:wrap;gap:10px}` · `.tag{order:1}` ·
    // `.x{order:2;margin-left:auto}` · `.atx{order:3;flex:1 1 100%}`. Tailwind v4
    // compiles `max-[N]` EXCLUSIVE, so the ruled ≤560 edge is the literal 561.
    // The prototype's fifth rule, `a.cta{order:4}`, has NO target here — this
    // React banner carries no CTA link, so it is deliberately not ported.
    // `.tag{order:1}` IS ported for fidelity but is the one rule with no
    // observable effect: the tag is already the first child, so orders 2 and 3
    // on the dismiss and the copy produce the same sequence without it. Removing
    // it alone leaves 77-muster-tail green; the other four each fail it.
    <div
      data-testid="announce-banner"
      className="flex items-center gap-3.5 rounded-2xl bg-[linear-gradient(100deg,#ff5079,#ff1f4a_70%,#c7123a)] px-[18px] py-[13px] text-white shadow-[0_12px_28px_-16px_var(--red-glow)] max-[561px]:flex-wrap max-[561px]:gap-2.5"
    >
      {announcement.unread && (
        <span
          data-testid="announce-tag"
          className="shrink-0 rounded-md bg-white/20 px-2 py-[3px] text-[10px] font-bold tracking-[0.07em] uppercase max-[561px]:order-1"
        >
          New
        </span>
      )}
      <div
        data-testid="announce-text"
        className="min-w-0 flex-1 text-[13px] leading-snug max-[561px]:order-3 max-[561px]:flex-[1_1_100%]"
      >
        <b className="font-display">{announcement.header}</b>
        {announcement.body && (
          // The clamp is pre-existing desktop behaviour and stays there. At ≤560
          // it would cancel the ruled full-width line the body just won — one
          // line either way — so it is released inside the band only.
          <span className="line-clamp-1 text-white/85 max-[561px]:line-clamp-none">
            {" "}
            {announcement.body}
          </span>
        )}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="announce-dismiss"
        onClick={() => setDismissed(true)}
        className="shrink-0 rounded-md p-1 hover:bg-white/15 max-[561px]:order-2 max-[561px]:ml-auto"
      >
        <X className="size-4" strokeWidth={2.2} />
      </button>
    </div>
  )
}

// ---- insider surfacing ----------------------------------------------------

// Atrium item 2 — additive Insider surfacing (D8: NOT the Phase-3 dashboard
// rider). Qualitative only, no dollar figure (D1). A member gets a small chip
// next to the greeting; a non-member gets the slim dark upsell banner below it.
// Both deep-link /insider.

function InsiderGreetingChip() {
  return (
    <Link
      href="/insider"
      className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-bold text-primary no-underline transition-colors hover:bg-primary/15"
    >
      <Lock className="size-3" strokeWidth={2.2} /> Insider
    </Link>
  )
}

function InsiderUpsell() {
  return (
    // MOBILE-PLAN :47 — the upsell is the second of the three ruled banners.
    // Prototype `@media (max-width:560px)` (customer-dashboard-light.html
    // :333-334): `.ins-upsell{flex-wrap:wrap}` · `.bx{flex:1 1 calc(100% - 60px)}`
    // — the identical pair already carried by the resume banner's `.tx`. The
    // text block claims the rest of line one beside the icon; the trailing
    // `shrink-0` arrow takes line two instead of crushing the copy.
    <Link
      href="/insider"
      data-testid="upsell-banner"
      className="flex items-center gap-3.5 rounded-2xl border border-[#26211f] bg-[linear-gradient(120deg,#1a1a17,#26211f)] px-5 py-[13px] text-[#f4f4f2] transition-shadow max-[561px]:flex-wrap hover:shadow-[0_12px_28px_-16px_rgba(0,0,0,0.4)]"
    >
      <div className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-white/10 bg-white/[0.08]">
        <Lock className="size-[18px] text-white" strokeWidth={1.9} />
      </div>
      <div
        data-testid="upsell-text"
        className="min-w-0 flex-1 max-[561px]:flex-[1_1_calc(100%_-_60px)]"
      >
        <h3 className="font-display text-[13.5px] font-bold">
          Lock in Insider pricing
        </h3>
        <p className="mt-0.5 text-[12px] text-white/60">
          Pay the previous price list on every order and stay one step behind
          every price increase.
        </p>
      </div>
      <ArrowRight className="size-[18px] shrink-0 text-white/60" strokeWidth={2.2} />
    </Link>
  )
}

// ---- launchpad ------------------------------------------------------------

// Book Bundles: the featured, dark banner above the service grid. Links into
// Post a Project (BUNDLE.href = "/post").
function BundleBanner() {
  const { Icon } = BUNDLE
  return (
    <Link
      href={BUNDLE.href}
      className="mt-[18px] flex items-center gap-4 rounded-2xl border border-[#26211f] bg-[linear-gradient(120deg,#1a1a17,#26211f)] px-5 py-4 text-[#f4f4f2] transition-shadow hover:shadow-[0_14px_30px_-18px_rgba(0,0,0,0.4)]"
    >
      <div className="grid size-11 shrink-0 place-items-center rounded-[11px] border border-white/10 bg-white/[0.08]">
        <Icon className="size-[22px] text-white" strokeWidth={1.7} />
      </div>
      <div className="min-w-0 flex-1">
        <span className="mb-1.5 inline-block rounded-md bg-primary px-2 py-[3px] text-[10px] font-bold tracking-[0.06em] text-primary-foreground uppercase">
          {BUNDLE.tag}
        </span>
        <h3 className="font-display text-base font-bold">{BUNDLE.name}</h3>
        <p className="mt-[3px] text-[12.5px] text-white/60">{BUNDLE.blurb}</p>
      </div>
      <ArrowRight className="size-5 shrink-0 text-white/60" strokeWidth={2.2} />
    </Link>
  )
}

// New-customer launchpad: a price-forward 4-up grid. Each tile deep-links into
// Post a Project's focused arrival (plain — no name pre-fill for a first-timer).
// Prices come from the server (real PriceList).
function ServiceGrid({ services, prices }) {
  return (
    // MOBILE-PLAN :47 launchpad ladder, as a monotonic `min-` cascade so each
    // step simply supersedes the last: 1 col ≤560 · 2 ≤860 · 3 ≤1020 · 4 above.
    // Tailwind v4 compiles `min-[N]` INCLUSIVE, so every literal is the ruled
    // edge + 1. Replaces `md:grid-cols-4` (768 is not a ruled edge).
    <div
      className="mt-3 grid grid-cols-1 gap-3 min-[561px]:grid-cols-2 min-[861px]:grid-cols-3 min-[1021px]:grid-cols-4"
      data-testid="launchpad-grid"
    >
      {services.map((s) => {
        const { Icon } = s
        const price = prices?.[s.key]
        return (
          <Link
            key={s.key}
            href={serviceHref(s)}
            data-testid="launchpad-tile"
            // ≤560 the tile is one per row, so the 150px floor that keeps the
            // 4-up grid even just adds dead space (prototype `.lpd .t{min-height:0}`).
            className="flex min-h-[150px] flex-col gap-[13px] rounded-2xl border border-border bg-card p-4 pt-[18px] transition-shadow max-[561px]:min-h-0 hover:shadow-[0_14px_30px_-18px_rgba(0,0,0,0.3)]"
          >
            <div className="flex items-center gap-2.5">
              <div className="grid size-[34px] shrink-0 place-items-center rounded-[9px] border border-border bg-secondary">
                <Icon className="size-[18px] text-foreground" strokeWidth={1.7} />
              </div>
              <h3 className="font-display text-[13.5px] font-bold">{s.name}</h3>
            </div>
            {price?.amount ? (
              <div className="mt-auto">
                {price.from && (
                  <div className="text-[10px] font-bold tracking-[0.07em] text-ink-3 uppercase">
                    From
                  </div>
                )}
                <div className="mt-[3px] font-display text-[25px] leading-none font-bold tracking-[-1px]">
                  {price.amount}
                </div>
                {price.unit && (
                  <div className="mt-1 text-[11px] font-semibold text-ink-3">
                    {price.unit}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-auto text-[11px] font-semibold text-ink-3">
                See pricing
              </div>
            )}
          </Link>
        )
      })}
    </div>
  )
}

// Returning-customer launchpad: compact 2-col rows. Repeat customers get the
// name pre-filled on arrival, so these links carry &repeat=1 (Book Writing
// excepted — see serviceHref).
function ServiceRows({ services, prices }) {
  return (
    // MOBILE-PLAN :47 — this launchpad IS governed. Prototype `.lpb` is
    // `grid-template-columns:1fr 1fr` (customer-dashboard-light.html :280) folded
    // to `1fr` inside `@media (max-width:860px)` (:315): ONE column at ≤860, TWO
    // above it. `lg:` is 1024 and is not a ruled edge — it ran a single column
    // through the whole 861–1023 band. Tailwind v4 compiles `min-[N]` INCLUSIVE,
    // so the ruled ≤860 edge is the literal 861.
    <div
      className="mt-3 grid grid-cols-1 gap-2 min-[861px]:grid-cols-2"
      data-testid="launchpad-rows"
    >
      {services.map((s) => {
        const { Icon } = s
        const price = prices?.[s.key]
        return (
          <Link
            key={s.key}
            href={serviceHref(s, { repeat: true })}
            className="group flex items-center gap-[13px] rounded-[13px] border border-border bg-card px-[15px] py-[13px] transition-shadow hover:shadow-[0_8px_20px_-16px_rgba(0,0,0,0.3)]"
          >
            <div className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-border bg-secondary">
              <Icon className="size-[18px] text-foreground" strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-display text-[13.5px] font-bold tracking-[-0.1px]">
                {s.name}
              </h3>
              <p className="truncate text-[11px] text-ink-3">{s.blurb}</p>
            </div>
            {price?.amount && (
              <div className="shrink-0 text-right">
                <b className="block font-display text-sm font-bold tracking-[-0.3px]">
                  {price.amount}
                </b>
                <span className="text-[10px] font-semibold text-ink-3">
                  {price.unit ?? (price.from ? "starting" : "")}
                </span>
              </div>
            )}
            <ChevronRight className="size-4 shrink-0 text-ink-3" strokeWidth={2.2} />
          </Link>
        )
      })}
    </div>
  )
}

function Launchpad({ state, pricing }) {
  const isNew = state === "new"
  const prices = pricing?.prices
  // Narration is a hard feature gate (Subscriptions::Features::NARRATION), not
  // Insider pricing — a customer without it can't post one, so the tile is
  // hidden from the launchpad rather than dead-ending the deep-link.
  const services =
    pricing?.canPostNarration === false
      ? SERVICES.filter((s) => s.key !== "narration")
      : SERVICES
  return (
    <section>
      {/* MOBILE-PLAN :47 — ≤560 the launchpad head stacks (prototype
          `.lp-head{flex-direction:column;align-items:flex-start;gap:8px}`).
          Until it does, the `shrink-0` "Browse all services" link holds a
          162px column open and pushes itself off the right edge at 365. */}
      <div
        className="mb-0.5 flex items-end justify-between gap-4 max-[561px]:flex-col max-[561px]:items-start max-[561px]:gap-2"
        data-testid="launchpad-head"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h2 className="font-display text-[23px] font-bold tracking-[-0.5px]">
              {isNew ? "What would you like to create?" : "Start a new project"}
            </h2>
            {pricing?.insider != null && (
              <span
                className={cn(
                  "flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold",
                  pricing.insider
                    ? "bg-primary/10 text-primary"
                    : "bg-secondary text-muted-foreground"
                )}
              >
                {pricing.insider && <Sparkles className="size-3" strokeWidth={2.2} />}
                {pricing.insider ? "Insider pricing" : "Standard pricing"}
              </span>
            )}
          </div>
          <p className="mt-1 max-w-[520px] text-[13.5px] leading-relaxed text-muted-foreground">
            Pick a service to hire a vetted freelancer. You'll set the details
            and see pricing before you pay
            {isNew ? " — no charge until you approve." : "."}
          </p>
        </div>
        <Link
          href="/post"
          data-testid="launchpad-browse"
          className="flex shrink-0 items-center gap-1.5 text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          Browse all services <ArrowRight className="size-[15px]" strokeWidth={2.2} />
        </Link>
      </div>

      <BundleBanner />
      {isNew ? (
        <ServiceGrid services={services} prices={prices} />
      ) : (
        <ServiceRows services={services} prices={prices} />
      )}
    </section>
  )
}

// ---- returning: continue your work ---------------------------------------

const METRICS = [
  { key: "approvals", label: "Awaiting your approval", dot: "var(--primary)" },
  { key: "active", label: "In progress", dot: "var(--chart-1)" },
]

function MetricStrip({ counts, links = {}, insiderMember }) {
  return (
    <div className="mb-4 flex flex-wrap gap-2.5">
      {METRICS.map((m) => (
        <Link
          key={m.key}
          href={links[m.key] ?? "/projects"}
          className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-[15px] py-2.5 transition-shadow hover:shadow-[0_8px_18px_-14px_rgba(0,0,0,0.3)]"
        >
          <span
            className="size-[7px] shrink-0 rounded-full"
            style={{ background: m.dot }}
          />
          <span className="font-display text-lg font-bold tracking-[-0.5px]">
            {counts?.[m.key] ?? 0}
          </span>
          <span className="text-[12.5px] font-semibold text-muted-foreground">
            {m.label}
          </span>
          <ChevronRight className="size-3.5 text-ink-3" strokeWidth={2.2} />
        </Link>
      ))}
      {/* Qualitative Insider stat (member only) — no number, deep-links /insider. */}
      {insiderMember && (
        <Link
          href="/insider"
          className="flex items-center gap-2.5 rounded-xl border border-primary/20 bg-primary/[0.06] px-[15px] py-2.5 transition-shadow hover:shadow-[0_8px_18px_-14px_rgba(0,0,0,0.3)]"
        >
          <Lock className="size-[15px] shrink-0 text-primary" strokeWidth={2.2} />
          <span className="text-[12.5px] font-semibold text-primary">
            Insider rates active
          </span>
          <ChevronRight className="size-3.5 text-primary/60" strokeWidth={2.2} />
        </Link>
      )}
    </div>
  )
}

function PanelRow({ href, title, subtitle, children }) {
  const body = (
    <>
      <div className="grid size-8 shrink-0 place-items-center rounded-[9px] border border-border bg-secondary">
        <FileText className="size-4 text-muted-foreground" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className="truncate font-display text-[13px] font-semibold">{title}</h4>
        <p className="mt-0.5 text-[11.5px] text-ink-3">{subtitle}</p>
      </div>
      {children}
    </>
  )

  const className =
    "flex items-center gap-3 border-b border-border px-[18px] py-[13px] last:border-b-0 transition-colors hover:bg-accent"

  return href ? (
    <Link href={href} className={className}>
      {body}
    </Link>
  ) : (
    <div className={cn(className, "cursor-default")}>{body}</div>
  )
}

function Panel({ title, count, items, kind, viewAllHref = "/projects" }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-[18px] py-[14px]">
        <h3 className="flex items-center gap-2 font-display text-[13.5px] font-bold">
          {title}
          <span
            className={cn(
              "rounded-full px-2 py-px text-[10.5px] font-bold",
              kind === "approvals"
                ? "bg-primary/10 text-primary"
                : "bg-secondary text-muted-foreground"
            )}
          >
            {count}
          </span>
        </h3>
        <Link
          href={viewAllHref}
          className="ml-auto text-[11.5px] font-semibold text-ink-3 hover:text-foreground"
        >
          View all
        </Link>
      </div>

      {items.length === 0 ? (
        <p className="px-[18px] py-8 text-center text-[12.5px] text-ink-3">
          {kind === "approvals"
            ? "Nothing needs your approval right now."
            : "No active projects."}
        </p>
      ) : (
        items.map((item) => (
          <PanelRow
            key={item.id}
            href={item.href ?? "/projects"}
            title={item.title}
            subtitle={item.subtitle}
          >
            {kind === "active" && item.progress && (
              <div className="w-[78px] shrink-0">
                <Progress value={item.progress.value} className="h-[5px]" />
                <div className="mt-1 text-right text-[10px] font-semibold text-ink-3">
                  {item.progress.value}%
                </div>
              </div>
            )}
          </PanelRow>
        ))
      )}
    </div>
  )
}

const HOW_IT_WORKS = [
  {
    n: 1,
    title: "Pick a service",
    body: "Choose what you need and answer a few quick questions about your project.",
  },
  {
    n: 2,
    title: "Get matched & approve",
    body: "We pair you with a vetted freelancer. Review drafts and approve each milestone.",
  },
  {
    n: 3,
    title: "Receive your files",
    body: "Get final, ready-to-publish files — with revisions until you're happy.",
  },
]

function HowItWorks() {
  return (
    <section>
      <div className="mb-[26px] h-px bg-border" />
      {/* MOBILE-PLAN :47 — ruled 1 column at ≤860 (prototype `.how`). `md:` is
          768 and is not a ruled edge, so the step moves to the ruled 861. */}
      <div className="grid gap-3.5 min-[861px]:grid-cols-3" data-testid="how-it-works">
        {HOW_IT_WORKS.map((step) => (
          <div key={step.n} className="flex items-start gap-3">
            <span className="grid size-[26px] shrink-0 place-items-center rounded-full bg-foreground font-display text-[12.5px] font-bold text-background">
              {step.n}
            </span>
            <div>
              <h4 className="font-display text-[13.5px] font-bold">{step.title}</h4>
              <p className="mt-0.5 text-xs leading-snug text-ink-3">{step.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function EmptyProjects() {
  return (
    <section>
      <div className="mb-3.5 flex items-center justify-between">
        <h2 className="font-display text-base font-bold tracking-[-0.2px]">
          Your projects
        </h2>
      </div>
      <div className="rounded-2xl border border-dashed border-line-2 bg-card p-[34px] text-center">
        <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-[13px] bg-secondary">
          <FolderOpen className="size-[23px] text-ink-3" strokeWidth={1.7} />
        </div>
        <h3 className="font-display text-[15px] font-bold">No projects yet</h3>
        <p className="mx-auto mt-1.5 max-w-[360px] text-[13px] leading-relaxed text-ink-3">
          Once you start a project, you'll track drafts, approvals and messages
          right here.
        </p>
      </div>
    </section>
  )
}

// ---- page -----------------------------------------------------------------

export default function Dashboard({ dashboard = {} }) {
  const { auth } = usePage().props
  const {
    state = "new",
    counts = {},
    links = {},
    approvals = [],
    active = [],
    launchpad,
    announcement,
    promotion = null,
    insider = {},
    draft = null,
    draftCount = 0,
  } = dashboard

  const subline =
    state === "new"
      ? "Let's get your first project started"
      : `${counts.approvals ?? 0} ${
          counts.approvals === 1 ? "approval" : "approvals"
        } waiting · ${counts.active ?? 0} active ${
          counts.active === 1 ? "project" : "projects"
        }`

  return (
    <>
      <div className="mx-auto flex max-w-[1040px] flex-col gap-[30px] px-8 pt-[34px] pb-14">
        <AnnouncementBanner announcement={announcement} />

        <DashboardPromo promo={promotion} />

        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-display text-xl font-bold tracking-[-0.3px]">
              {greetingFor(auth?.firstName)}
            </h1>
            {insider.member && <InsiderGreetingChip />}
          </div>
          <p className="mt-0.5 text-[12.5px] text-ink-3">{subline}</p>
        </div>

        {!insider.member && <InsiderUpsell />}

        <ResumeDraftBanner
          draft={draft}
          variant={state}
          moreCount={Math.max(0, draftCount - 1)}
        />

        <Launchpad state={state} pricing={launchpad} />

        {state === "new" ? (
          <>
            <HowItWorks />
            <EmptyProjects />
          </>
        ) : (
          <section>
            <MetricStrip counts={counts} links={links} insiderMember={insider.member} />
            <div className="mb-3.5 flex items-center justify-between">
              <h2 className="font-display text-base font-bold tracking-[-0.2px]">
                Continue your work
              </h2>
              <Link
                href="/projects"
                className="text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
              >
                All projects →
              </Link>
            </div>
            {/* MOBILE-PLAN :47 — ruled 1 column at ≤860 (prototype `.work`). */}
            <div className="grid gap-[18px] min-[861px]:grid-cols-2" data-testid="work-panels">
              <Panel
                title="Needs your approval"
                count={counts.approvals ?? 0}
                items={approvals}
                kind="approvals"
                viewAllHref={links.approvals ?? "/approvals"}
              />
              <Panel
                title="Active projects"
                count={counts.active ?? 0}
                items={active}
                kind="active"
                viewAllHref={links.active ?? "/projects"}
              />
            </div>
          </section>
        )}
      </div>
    </>
  )
}

Dashboard.layout = (page) => (
  <AppShell active="Dashboard" crumb={["Dashboard"]}>
    {page}
  </AppShell>
)
